import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpDown, Bookmark, ChevronDown, ChevronUp, Clock, GripVertical, Loader2, LocateFixed, MapPin, Mic, Navigation, Plus, Star, WifiOff, X } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { toast } from "sonner";
import { geocodeAutocomplete, type GeoSuggestion, resolveSuggestionCoords, needsResolve, endAutocompleteSession } from "@/api/routing";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import type { LngLat } from "@/utils/polyline";
import { SavedPlacesChips } from "@/components/SavedPlacesChips";
import { iconFor, loadSavedPlaces, type SavedPlace } from "@/utils/savedPlaces";
import { loadRecentSearches, type RecentSearch } from "@/utils/recentSearches";
import type { HeartedPlace } from "@/utils/heartedPlaces";
import { deleteSavedTrip, type SavedTrip, type SavedTripSettings } from "@/utils/savedTrips";
import { useAuth } from "@/hooks/useAuth";
// (duplicate `import { toast } from "sonner"` removed Aug 8 — it was
//  shadowing itself and tripping TS2300 on every typecheck.)

export interface TripPoint {
  name: string;
  place: string;
  coords: LngLat;
}

interface Props {
  /** User's GPS, used as the default "current location" start. */
  userLocation?: LngLat;
  /** Currently set start (null = use current location). */
  start: TripPoint | null;
  end: TripPoint | null;
  /** Intermediate stops (in order between start and end). */
  stops?: TripPoint[];
  onChange: (next: { start: TripPoint | null; end: TripPoint | null; stops: TripPoint[] }) => void;
  /** Called when the user has both points and wants to compute routes.
   *  `startPoint` is the resolved origin place, or null when the origin is the
   *  user's GPS location (so the host can tell a typed start from "current
   *  location" instead of guessing). */
  onSubmit: (start: LngLat, end: TripPoint, stops: TripPoint[], startPoint?: TripPoint | null) => void;
  onBack?: () => void;
  loading?: boolean;
  /** Tap "Edit" + chip → host opens the rename/delete dialog. */
  onEditSavedPlace?: (place: SavedPlace) => void;
  /** Tap "+ Label place" → host opens the create dialog. */
  onAddSavedPlace?: () => void;
  /** Tap the bookmark icon next to a row → label that specific point.
   *  Receives the resolved TripPoint for that row (start/end/stop). */
  onLabelPoint?: (point: TripPoint) => void;
  /** Current routing settings — snapshotted into a saved trip so re-running
   *  it always produces the same kind of route. */
  currentSettings?: SavedTripSettings;
  /** Called when the user picks a saved trip that has settings attached, so
   *  the host page can restore mode/profile/tolerance/loves before routing. */
  onApplyTripSettings?: (settings: SavedTripSettings) => void;
  /** Trigger the geolocation permission flow. Called when the user taps the
   *  "Use current location" row before we have a GPS fix. */
  onRequestLocation?: () => void;
  /** True while a permission request is in-flight. */
  isRequestingLocation?: boolean;
  /** Current geolocation permission state, so we can show a denied hint. */
  locationStatus?: "idle" | "prompt" | "granted" | "denied" | "unavailable" | "timeout" | "error" | "requesting";
}

type Field = { kind: "start" } | { kind: "end" } | { kind: "stop"; index: number } | null;

/**
 * Multi-stop trip planner: start + optional intermediate stops + end with autocomplete.
 * Includes a reverse button to flip the route direction.
 */
export const TripSearch = ({
  userLocation,
  start,
  end,
  stops = [],
  onChange,
  onSubmit,
  onBack,
  loading,
  onEditSavedPlace,
  onAddSavedPlace,
  onLabelPoint,
  currentSettings,
  onApplyTripSettings,
  onRequestLocation,
  isRequestingLocation,
  locationStatus,
}: Props) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const offline = !isOnline;
  const [active, setActive] = useState<Field>(null);
  // WHAT THE USER ACTUALLY PICKED (Morgan, Aug 10, reported repeatedly before
  // that: "I put in a route and it says it can't find it — I need to fiddle to
  // get the map to move to the starting location").
  //
  // The submit guard below used to decide "did they pick a suggestion?" by
  // comparing the text in the box to the resolved point's `name`/`place` with
  // string equality. Any drift — a comma, a formatting difference, a prop
  // round-trip that rewrites the field — makes that false, so a start the user
  // HAD selected got thrown away and re-geocoded from scratch. When the
  // re-geocode returned nothing (a formatted address autocompletes far worse
  // than what someone types) they got "Couldn't find that starting point" for a
  // place they had just chosen off the list. Retyping until the strings happen
  // to match is exactly the "fiddling" in the report.
  //
  // Selection is a FACT this component observes. Record it instead of trying to
  // reconstruct it afterwards from display strings.
  const resolvedStartTextRef = useRef<string | null>(null);
  const resolvedEndTextRef = useRef<string | null>(null);
  const [startText, setStartText] = useState(start?.place ?? "");
  const [endText, setEndText] = useState(end?.place ?? "");
  const [stopTexts, setStopTexts] = useState<string[]>(stops.map((s) => s.place));
  const [suggestions, setSuggestions] = useState<GeoSuggestion[]>([]);
  const [busy, setBusy] = useState(false);
  /** Native dictation in progress (phone mic — Morgan's friction batch). */
  const [dictating, setDictating] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const debounceRef = useRef<number | undefined>();
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  /**
   * Reorder a row in the unified [start, stop_0, …, stop_N-1, destination]
   * list. Index 0 is the start, 1..N are stops, N+1 is the destination.
   * Dragging the start down demotes it to a stop (and promotes whatever
   * lands at index 0 to be the new start). Dragging the destination up
   * works analogously. If the resulting start slot has no resolved point,
   * we fall back to GPS by setting start to null.
   */
  const reorderRow = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0) return;
    const endIdx = stopTexts.length + 1;
    if (from > endIdx || to > endIdx) return;

    const empty: TripPoint = { name: "", place: "", coords: [0, 0] as LngLat };
    // Build the unified list: [start, ...stops, end].
    const points: TripPoint[] = [];
    const texts: string[] = [];
    points.push(start ?? empty);
    texts.push(startText && startText !== "Current location" ? startText : "");
    for (let i = 0; i < stopTexts.length; i++) {
      points.push(stops[i] ?? empty);
      texts.push(stopTexts[i]);
    }
    points.push(end ?? empty);
    texts.push(endText);

    const [movedPoint] = points.splice(from, 1);
    points.splice(to, 0, movedPoint);
    const [movedText] = texts.splice(from, 1);
    texts.splice(to, 0, movedText);

    const isResolved = (p: TripPoint) => p.coords[0] !== 0 || p.coords[1] !== 0;

    const newStartPoint = points[0];
    const newStartText = texts[0];
    const newEnd = points[points.length - 1];
    const newEndText = texts[texts.length - 1];
    const newStopPoints = points.slice(1, -1);
    const newStopTexts = texts.slice(1, -1);

    const trimmedStops = newStopPoints.filter(isResolved);
    const newStartResolved = isResolved(newStartPoint) ? newStartPoint : null;
    const newEndResolved = isResolved(newEnd) ? newEnd : null;

    setStopTexts(newStopTexts);
    setEndText(newEndText);
    setStartText(newStartResolved ? newStartText : (userLocation ? "Current location" : ""));
    onChange({ start: newStartResolved, end: newEndResolved, stops: trimmedStops });
  };

  // Live mirror of saved places — re-reads on the device-storage change event
  // so newly saved/renamed places appear in matches immediately.
  const [savedPlaces, setSavedPlaces] = useState<SavedPlace[]>(() => loadSavedPlaces());
  useEffect(() => {
    const refresh = () => setSavedPlaces(loadSavedPlaces());
    window.addEventListener("saved-places-changed", refresh);
    return () => window.removeEventListener("saved-places-changed", refresh);
  }, []);

  // Live mirror of recent searches (destinations the user actually routed to).
  const [recents, setRecents] = useState<RecentSearch[]>(() => loadRecentSearches());
  useEffect(() => {
    const refresh = () => setRecents(loadRecentSearches());
    window.addEventListener("recent-searches-changed", refresh);
    return () => window.removeEventListener("recent-searches-changed", refresh);
  }, []);

  // Keep displayed text in sync when parent changes. Prefer the friendly
  // `name` (e.g. "Home", "Central Park") over the raw `place` field, which
  // for hearted places without a note can be a "lng, lat" string.
  // Show the FULL resolved place (with city/state), not just the short name —
  // so a mis-geocode (e.g. "Bowdoin" landing near NYC instead of Bowdoin
  // College, ME) is visible in the field instead of hidden behind one word.
  useEffect(() => {
    if (start) { setStartText(start.place || start.name); return; }
    // start is unresolved. Do NOT clobber text the user is actively typing or
    // has pasted but not yet submitted — otherwise a GPS update (which changes
    // `userLocation` and re-runs this effect) silently replaces a pasted
    // address with "Current location". Only fall back to the placeholder when
    // the field is idle and holds nothing the user entered.
    if (active?.kind === "start") return;
    setStartText((prev) =>
      prev && prev !== "Current location" ? prev : userLocation ? "Current location" : "",
    );
  }, [start?.name, start?.place, userLocation, active]);
  useEffect(() => { setEndText(end ? (end.place || end.name) : ""); }, [end?.name, end?.place]);
  useEffect(() => { setStopTexts(stops.map((s) => s.place || s.name)); }, [stops.map((s) => `${s.name}|${s.place}`).join("~")]); // eslint-disable-line react-hooks/exhaustive-deps

  const query = !active
    ? ""
    : active.kind === "start"
    ? startText
    : active.kind === "end"
    ? endText
    : stopTexts[active.index] ?? "";

  /** Saved places whose label matches the typed query (case-insensitive
   *  substring). Pinned to the top of the suggestions list so users can pick
   *  "Home" or "Mom" as a starting point/stop without re-geocoding. */
  const savedMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Empty field, just focused → show EVERY preset up front (Morgan:
    // "'home' and preset destinations are still not obviously visible on
    // the search"). Home shouldn't require typing "home".
    if (q.length < 1) return savedPlaces.slice(0, 5);
    return savedPlaces
      .filter((p) => p.label.toLowerCase().includes(q))
      .slice(0, 4);
  }, [query, savedPlaces]);

  /** Recent destinations to surface. When the input is empty (just focused),
   *  show the latest 5 — quick re-pick of last week's destinations. When the
   *  user starts typing, filter by name/address substring. */
  const recentMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Hide any recent that exactly collides with a shown saved place — saved
    // places already lead the list, no point repeating.
    const savedCoords = new Set(
      savedMatches.map((s) => `${s.coords[0].toFixed(4)},${s.coords[1].toFixed(4)}`),
    );
    const candidates = recents.filter(
      (r) => !savedCoords.has(`${r.coords[0].toFixed(4)},${r.coords[1].toFixed(4)}`),
    );
    if (q.length === 0) return candidates.slice(0, 5);
    return candidates
      .filter((r) => r.place.toLowerCase().includes(q) || r.name.toLowerCase().includes(q))
      .slice(0, 4);
  }, [query, recents, savedMatches]);

  // Track the last query we actually fetched so we don't re-fetch when
  // React re-renders with the same string (e.g. parent state changes).
  const lastFetchedRef = useRef<string>("");
  useEffect(() => {
    window.clearTimeout(debounceRef.current);
    if (!active) { setSuggestions([]); return; }
    if (offline) { setSuggestions([]); setBusy(false); return; }
    const trimmed = query.trim();
    // Cost guard: 3-char minimum + 400ms debounce. The Places autocomplete
    // session bills per call, so we only fire after the user actually
    // pauses typing on something substantive.
    if (trimmed.length < 3) { setSuggestions([]); setBusy(false); return; }
    if (trimmed === lastFetchedRef.current) return;
    setBusy(true);
    debounceRef.current = window.setTimeout(async () => {
      try {
        lastFetchedRef.current = trimmed;
        // Bias results toward the trip's start point when one is set (so a
        // local "Sleepy Hollow daycare" outranks a same-named place elsewhere),
        // falling back to the user's GPS location when there's no start yet.
        const bias = active?.kind === "start" ? userLocation : (start?.coords ?? userLocation);
        const r = await geocodeAutocomplete(trimmed, bias);
        // ONLY THE CURRENT QUERY MAY PAINT (Aug 20). geocodeAutocomplete has
        // no abort and is raced against a 10s timeout, which is exactly how
        // long a cold lookup can take. Type "san fr" (slow), continue to
        // "san francisco airport" (fast): the airport list appeared, then the
        // stale "san fr" list replaced it seconds later, under the user's
        // finger. lastFetchedRef already holds the newest query the effect
        // fired, so anything else arriving late is by definition superseded.
        if (trimmed !== lastFetchedRef.current) {
          console.log(`[autocomplete] dropped stale results for "${trimmed}" (now "${lastFetchedRef.current}")`);
          return;
        }
        setSuggestions(r);
      } finally {
        setBusy(false);
      }
    }, 400);
    return () => window.clearTimeout(debounceRef.current);
  }, [query, active, userLocation, offline]);

  const pick = async (s: GeoSuggestion) => {
    if (!active) return;
    // Resolve coords if Places returned this suggestion without them
    // (lazy details lookup happens only here, on explicit selection).
    let coords = s.center;
    if (needsResolve(s)) {
      coords = await resolveSuggestionCoords(s);
    }
    // End the autocomplete session so the next keystroke starts a fresh
    // one — Google bills "Autocomplete (per session)" as a single charge.
    endAutocompleteSession();
    lastFetchedRef.current = "";
    const point: TripPoint = { name: s.name, place: s.place, coords };
    // Auto-fire route generation when picking from autocomplete completes
    // a usable trip (origin + destination). Without this, users who pick
    // a destination from the dropdown see "Finding pleasant routes…" with
    // nothing happening because no Go was tapped.
    let autoSubmitEnd: TripPoint | null = null;
    let autoSubmitStartCoords: LngLat | undefined;
    // WHO the origin is, not just WHERE (Morgan, Aug 20: typed "New Buffalo"
    // as the start, the route still left from her real location). This
    // auto-submit used to omit the resolved start place, and the host reads
    // a missing start place as "GPS trip" — then OVERWRITES the coords with
    // a fresh GPS fix. The field said New Buffalo; the engine got Westchester.
    let autoSubmitStartPoint: TripPoint | null = null;
    if (active.kind === "start") {
      onChange({ start: point, end, stops });
      setStartText(s.place);
      resolvedStartTextRef.current = s.place;
      if (end) { autoSubmitEnd = end; autoSubmitStartCoords = point.coords; autoSubmitStartPoint = point; }
    } else if (active.kind === "end") {
      onChange({ start, end: point, stops });
      setEndText(s.place);
      resolvedEndTextRef.current = s.place;
      const startCoords = start?.coords ?? userLocation;
      if (startCoords) { autoSubmitEnd = point; autoSubmitStartCoords = startCoords; autoSubmitStartPoint = start ?? null; }
    } else {
      const nextStops = [...stops];
      nextStops[active.index] = point;
      onChange({ start, end, stops: nextStops });
      const nextTexts = [...stopTexts];
      nextTexts[active.index] = s.place;
      setStopTexts(nextTexts);
    }
    setActive(null);
    setSuggestions([]);
    if (autoSubmitEnd && autoSubmitStartCoords) {
      const finalEnd = autoSubmitEnd;
      const finalStart = autoSubmitStartCoords;
      const finalStartPoint = autoSubmitStartPoint;
      // Defer so parent state from onChange settles first. The 4th arg is
      // the resolved start PLACE — null means (and only means) "route from
      // GPS"; omitting it here was the New Buffalo bug above.
      setTimeout(() => onSubmit(finalStart, finalEnd, stops, finalStartPoint), 0);
    }
  };

  const applyCurrentLocation = () => {
    onChange({ start: null, end, stops });
    setStartText("Current location");
    setActive(null);
    setSuggestions([]);
    // One-tap re-plan (Morgan: "it's scary to be in traffic and not be able
    // to do this"): when a destination is already set, picking "Current
    // location" immediately re-runs the search from where the driver IS —
    // no extra Go tap needed. The selected route type carries over and the
    // map re-fits to the fresh routes, which is the reorientation that
    // matters.
    if (userLocation && end) {
      const finalEnd = end;
      const loc = userLocation;
      // Defer so parent state from onChange settles first (same pattern as
      // the auto-submit in pick()).
      setTimeout(() => onSubmit(loc, finalEnd, stops, null), 0);
    }
  };

  /** Tap a saved-place chip → fill whichever input is focused (Start/End/Stop).
   *  Falls back to End when nothing is focused. */
  const pickSaved = (p: SavedPlace) => {
    if (active) return applySavedToActive(p);
    const point: TripPoint = { name: p.label, place: p.place, coords: p.coords };
    onChange({ start, end: point, stops });
    setEndText(p.label);
    setActive(null);
    setSuggestions([]);
  };

  /** Tap a hearted-place chip → fill whichever input is focused. */
  const pickHearted = (h: HeartedPlace) => {
    const label = h.note?.trim() || `${h.coords[1].toFixed(3)}, ${h.coords[0].toFixed(3)}`;
    const point: TripPoint = { name: label, place: label, coords: h.coords };
    if (active?.kind === "start") {
      onChange({ start: point, end, stops });
      setStartText(label);
    } else if (active?.kind === "stop") {
      const nextStops = [...stops];
      nextStops[active.index] = point;
      onChange({ start, end, stops: nextStops });
      const nextTexts = [...stopTexts];
      nextTexts[active.index] = label;
      setStopTexts(nextTexts);
    } else {
      onChange({ start, end: point, stops });
      setEndText(label);
    }
    setActive(null);
    setSuggestions([]);
  };

  /** Tap a saved-trip chip → load start + end + stops in one shot, and
   *  restore the settings the trip was saved with so the route comes out
   *  the same way every time. */
  const pickTrip = (t: SavedTrip) => {
    onChange({
      start: { name: t.start.name, place: t.start.place, coords: t.start.coords },
      end: { name: t.end.name, place: t.end.place, coords: t.end.coords },
      stops: t.stops.map((s) => ({ name: s.name, place: s.place, coords: s.coords })),
    });
    setStartText(t.start.place);
    setEndText(t.end.place);
    setStopTexts(t.stops.map((s) => s.place));
    if (t.settings && onApplyTripSettings) onApplyTripSettings(t.settings);
    setActive(null);
    setSuggestions([]);
  };

  const removeTrip = (t: SavedTrip) => {
    deleteSavedTrip(t.id);
    toast.success(`Removed “${t.label}”`);
  };

  /** Apply a saved place to whichever input is currently focused. The visible
   *  text becomes the friendly label ("Home") while the underlying TripPoint
   *  carries the real address + coords for routing. */
  const applySavedToActive = (p: SavedPlace) => {
    if (!active) return;
    const point: TripPoint = { name: p.label, place: p.place, coords: p.coords };
    if (active.kind === "start") {
      onChange({ start: point, end, stops });
      setStartText(p.label);
    } else if (active.kind === "end") {
      onChange({ start, end: point, stops });
      setEndText(p.label);
    } else {
      const nextStops = [...stops];
      nextStops[active.index] = point;
      onChange({ start, end, stops: nextStops });
      const nextTexts = [...stopTexts];
      nextTexts[active.index] = p.label;
      setStopTexts(nextTexts);
    }
    setActive(null);
    setSuggestions([]);
  };

  /** Apply a recent search to the focused input. */
  const applyRecentToActive = (r: RecentSearch) => {
    if (!active) return;
    const point: TripPoint = { name: r.name, place: r.place, coords: r.coords };
    if (active.kind === "start") {
      onChange({ start: point, end, stops });
      setStartText(r.place);
    } else if (active.kind === "end") {
      onChange({ start, end: point, stops });
      setEndText(r.place);
    } else {
      const nextStops = [...stops];
      nextStops[active.index] = point;
      onChange({ start, end, stops: nextStops });
      const nextTexts = [...stopTexts];
      nextTexts[active.index] = r.place;
      setStopTexts(nextTexts);
    }
    setActive(null);
    setSuggestions([]);
  };

  /** Swap start <-> end and reverse intermediate stops. */
  const reverse = () => {
    // `start === null` means "use current location" (Props docstring); there's
    // no equivalent sentinel for `end`. The old code swapped `start` straight
    // into `end`, so a GPS-origin trip put `null` in the destination slot --
    // losing the destination entirely ("Laird Park to nowhere"). Snapshot the
    // live GPS fix into a real TripPoint instead, the same way Index.tsx does
    // elsewhere for a "Current location" chip, so the destination's data
    // survives the swap.
    if (!start && !end) return;
    const oldStart: TripPoint | null =
      start ?? (userLocation ? { name: "Current location", place: "Current location", coords: userLocation } : null);
    // Swapping a "Current location" point back INTO the start slot should
    // restore the null/GPS sentinel, not freeze it as a resolved point --
    // otherwise a second tap pins the start to wherever the GPS fix was on
    // the first tap instead of tracking the driver live.
    const newStart = end && end.name === "Current location" && end.place === "Current location" ? null : end;
    const newEnd = oldStart;
    const newStops = [...stops].reverse();
    onChange({ start: newStart, end: newEnd, stops: newStops });
    // The startText sync effect (above) only resets stale text back to
    // "Current location" when the PREVIOUS text was that literal string --
    // it's protecting text the user is mid-typing from a GPS update, but
    // that guard also leaves a just-swapped-out resolved address on screen
    // when `newStart` flips back to the GPS sentinel ("Laird Park" stays
    // showing instead of "Current location"). Set it explicitly here, the
    // same way applyCurrentLocation() already does for the same transition.
    setStartText(newStart ? newStart.place || newStart.name : userLocation ? "Current location" : "");
    setActive(null);
    setSuggestions([]);
  };

  const addStop = () => {
    const nextStops = [...stops, { name: "", place: "", coords: [0, 0] as LngLat }];
    // Don't actually add an empty point to parent yet — keep stops as-is and just open a draft row.
    // We track draft via stopTexts length > stops.length.
    setStopTexts([...stopTexts, ""]);
    setActive({ kind: "stop", index: stopTexts.length });
    void nextStops;
  };

  const removeStop = (index: number) => {
    const nextStops = stops.filter((_, i) => i !== index);
    const nextTexts = stopTexts.filter((_, i) => i !== index);
    setStopTexts(nextTexts);
    onChange({ start, end, stops: nextStops });
    setActive(null);
  };

  // The Go button is enabled as long as we have at least one filled
  // location AND a usable origin. If the user only typed something in the
  // top "start" field (a very common mistake — people read it as the
  // destination), we treat that single typed place as where they're going
  // and fall back to their GPS location for the origin.
  // The button is usable as soon as there's a destination to work with — a
  // resolved end, a resolved start (which we treat as the destination), OR
  // text the user typed into the destination field (we resolve it on Go).
  //
  // ORIGIN IS NOT A PRECONDITION (Morgan, Aug 30: "the arrow button does
  // nothing"). This used to also require `start || userLocation`, so a phone
  // that could not get a fix — `locationUnavailable`, thirty getCurrentPosition
  // calls, no position — left the arrow disabled with no explanation, and the
  // user could not tell a dead button from a broken app. Nothing downstream
  // needed the guard: handleSearchSubmit already waits up to 20s for a cold
  // fix and then refuses a null-island origin with the honest toast ("Couldn't
  // get your location — check location access for Pleasant in Settings, or
  // type a starting address"). Disabling the button was the only thing
  // stopping the user from ever reaching that message. Let the tap through.
  const canGo = Boolean(end || start || endText.trim()) && !offline;

  const handleGo = async () => {
    if (offline) return;
    // Resolve "where they're going" and "where they're starting from".
    // Priority: explicit end > the only typed place. Origin: explicit start
    // > GPS. If start and end are the same place (user only filled one
    // field), use GPS as origin.
    let effectiveEnd = end;
    let effectiveStart = start;

    // The user typed a destination but never picked a suggestion (or edited the
    // text after picking, leaving `end` stale). Resolve the typed text now —
    // otherwise we used to silently ignore it and route start→start. If we
    // can't find it, tell them rather than route somewhere wrong.
    const typedEnd = endText.trim();
    const endMatchesTyped =
      !!effectiveEnd &&
      (resolvedEndTextRef.current === typedEnd ||
        effectiveEnd.place === typedEnd ||
        effectiveEnd.name === typedEnd);
    if (typedEnd && !endMatchesTyped) {
      try {
        const sugg = await geocodeAutocomplete(typedEnd, userLocation);
        if (sugg.length > 0) {
          const s = sugg[0];
          const coords = needsResolve(s) ? await resolveSuggestionCoords(s) : s.center;
          endAutocompleteSession();
          effectiveEnd = { name: s.name, place: s.place, coords };
        } else {
          toast.error("Couldn't find that destination", { description: "Try picking one from the list." });
          return;
        }
      } catch {
        toast.error("Couldn't look up that destination", { description: "Check your connection and try again." });
        return;
      }
    }

    // Same for the START field: the user typed a starting point but never
    // picked a suggestion (or edited the text after picking, leaving `start`
    // stale). Resolve the typed text now. Without this, a typed-but-unresolved
    // start silently fell back to GPS — so a "Carmel → Ragged Point" trip would
    // route from the user's current location instead, shooting the line across
    // the country. We skip this only when the field is empty or literally
    // reads "Current location" (both genuinely mean "use my GPS").
    const typedStart = startText.trim();
    const isCurrentLocationText = typedStart === "" || typedStart.toLowerCase() === "current location";
    const startMatchesTyped =
      !!effectiveStart &&
      (resolvedStartTextRef.current === typedStart ||
        effectiveStart.place === typedStart ||
        effectiveStart.name === typedStart);
    if (typedStart && !isCurrentLocationText && !startMatchesTyped) {
      try {
        const sugg = await geocodeAutocomplete(typedStart, userLocation);
        if (sugg.length > 0) {
          const s = sugg[0];
          const coords = needsResolve(s) ? await resolveSuggestionCoords(s) : s.center;
          endAutocompleteSession();
          effectiveStart = { name: s.name, place: s.place, coords };
        } else {
          toast.error("Couldn't find that starting point", { description: "Try picking one from the list." });
          return;
        }
      } catch {
        toast.error("Couldn't look up that starting point", { description: "Check your connection and try again." });
        return;
      }
    }

    if (!effectiveEnd && effectiveStart) {
      // Only the start field was filled — treat it as the destination.
      effectiveEnd = effectiveStart;
      effectiveStart = null;
    }
    if (!effectiveEnd) return;
    const startCoords = effectiveStart?.coords ?? userLocation;
    if (!startCoords) return;
    // Drop focus so the suggestions/recents dropdown closes immediately
    // after submitting — otherwise it lingers over the map.
    setActive(null);
    if (typeof document !== "undefined") {
      (document.activeElement as HTMLElement | null)?.blur?.();
    }
    // Hand the resolved origin place through so the host doesn't have to guess
    // from its own (possibly-stale) start state whether this is a GPS trip.
    onSubmit(startCoords, effectiveEnd, stops, effectiveStart ?? null);
  };

  const startPlaceholder = "Choose starting point";

  const hasStops = stopTexts.length > 0;
  const isCollapsed = collapsed && hasStops && !active;

  // When the user has added stops, clicking anywhere outside the trip card
  // should collapse it down to the compact bar so the map underneath is
  // visible. We only auto-collapse when there are stops AND no input is
  // focused (active === null) — otherwise typing somewhere else would close
  // the autocomplete mid-edit.
  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!hasStops || isCollapsed || active !== null) return;
    const onDocPointerDown = (e: PointerEvent) => {
      const el = cardRef.current;
      if (!el) return;
      if (e.target instanceof Node && el.contains(e.target)) return;
      setCollapsed(true);
    };
    document.addEventListener("pointerdown", onDocPointerDown);
    return () => document.removeEventListener("pointerdown", onDocPointerDown);
  }, [hasStops, isCollapsed, active]);

  // DISMISS THE SUGGESTIONS / RECENTS DROPDOWN (Morgan, Aug 22: "Recent also
  // sticks on the screen for too long").
  //
  // It was not lingering - nothing ever closed it. `active` is set on focus and
  // cleared ONLY by explicit actions (picking a suggestion, submitting,
  // collapsing), and the effect directly above deliberately bails while
  // `active !== null` so that typing elsewhere cannot close the autocomplete
  // mid-edit. Between them, a focused field left the dropdown up until the user
  // chose something. This is the missing case, and nothing else changes.
  //
  // WHY pointerdown AND NOT onBlur. A blur handler fires BEFORE the list item's
  // click, so clearing `active` there unmounts the row under the finger and the
  // selection is swallowed. That is why the rows already carry
  // onMouseDown/preventDefault. Containment against cardRef means a press on a
  // row is never "outside", so those guards keep working and are untouched.
  //
  // Focus is dropped alongside `active` so the two cannot disagree: leaving the
  // input focused with `active === null` would mean the next keystroke typed
  // into a field whose dropdown refuses to reopen.
  useEffect(() => {
    if (active === null) return;
    const dismiss = () => {
      const focused = document.activeElement;
      if (focused instanceof HTMLElement && cardRef.current?.contains(focused)) {
        focused.blur();
      }
      setActive(null);
    };
    const onDocPointerDown = (e: PointerEvent) => {
      const el = cardRef.current;
      if (!el) return;
      if (e.target instanceof Node && el.contains(e.target)) return;
      dismiss();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismiss();
    };
    document.addEventListener("pointerdown", onDocPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onDocPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [active]);

  if (isCollapsed) {
    const startLabel = start?.name || (userLocation ? "Current location" : "Start");
    const endLabel = end?.name || "Destination";
    return (
      <div className="w-full" ref={cardRef}>
        <div className="bg-card rounded-full shadow-elevated border border-border flex items-center pl-4 pr-1 h-12 gap-2">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              aria-label="Cancel trip"
              title="Cancel trip"
              className="w-8 h-8 -ml-2 rounded-full hover:bg-muted flex items-center justify-center flex-shrink-0"
            >
              {/* When collapsed, the back arrow becomes an X — the next press
               *  cancels the whole trip rather than minimizing again. */}
              <X className="w-4 h-4" />
            </button>
          )}
          <MapPin className="w-4 h-4 text-pleasant flex-shrink-0" fill="currentColor" />
          <div className="flex-1 min-w-0 text-sm truncate">
            <span className="font-medium">{startLabel}</span>
            <span className="text-muted-foreground"> → </span>
            <span className="font-medium">{endLabel}</span>
            <span className="text-muted-foreground"> · {stopTexts.length} stop{stopTexts.length === 1 ? "" : "s"}</span>
          </div>
          <button
            type="button"
            onClick={() => setCollapsed(false)}
            aria-label="Expand stops"
            title="Show all stops"
            className="w-9 h-9 rounded-full hover:bg-muted flex items-center justify-center flex-shrink-0 text-muted-foreground"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={handleGo}
            disabled={!canGo || loading}
            aria-label={offline ? "Routes paused — offline" : "Find routes"}
            title={offline ? "You're offline — reconnect to plan a route" : "Find routes"}
            className="w-10 h-10 rounded-full bg-pleasant text-primary-foreground disabled:bg-pleasant/70 disabled:cursor-not-allowed hover:brightness-110 transition-smooth flex items-center justify-center flex-shrink-0"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full" ref={cardRef}>
      <div className="bg-card rounded-3xl shadow-elevated border border-border overflow-hidden">
        {offline && (
          <div
            role="status"
            aria-live="polite"
            className="flex items-center gap-2 px-4 py-2 bg-amber-500/10 text-amber-700 dark:text-amber-300 border-b border-amber-500/20 text-xs"
          >
            <WifiOff className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="leading-snug">
              You're offline — address search and route planning are paused until you reconnect.
            </span>
          </div>
        )}
        <div className="flex items-stretch relative">
          {onBack && (
            <button
              type="button"
              // When the trip already has stops, the back arrow first
              // MINIMIZES the panel (so the user can peek at the map without
              // losing their stops). Only when collapsed does the same arrow
              // — now an X — actually cancel the trip.
              onClick={() => {
                if (hasStops) {
                  setActive(null);
                  setCollapsed(true);
                } else {
                  onBack();
                }
              }}
              aria-label={hasStops ? "Minimize trip" : "Back"}
              title={hasStops ? "Minimize" : "Back"}
              className="w-12 flex items-center justify-center hover:bg-muted flex-shrink-0 border-r border-border"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}

          {/* Stacked inputs with a vertical pin track */}
          <div className="flex-1 min-w-0 relative">
            {/* Pin column */}
            <div className="absolute left-4 top-0 bottom-0 flex flex-col items-center pointer-events-none py-4">
              <div className="w-2.5 h-2.5 rounded-full bg-primary ring-2 ring-primary/20" />
              <div className="flex-1 w-px border-l border-dashed border-border my-1.5" />
              {stopTexts.map((_, i) => (
                <div key={`stop-marker-${i}`} className="flex flex-col items-center flex-1 w-px">
                  <div className="w-2 h-2 rounded-full bg-muted-foreground/60" />
                  <div className="flex-1 w-px border-l border-dashed border-border my-1.5" />
                </div>
              ))}
              <MapPin className="w-3.5 h-3.5 text-pleasant -mb-0.5" fill="currentColor" />
            </div>

            {/* Start row */}
            <div
              onDragOver={(e) => {
                if (dragIndex === null) return;
                e.preventDefault();
                if (dragOverIndex !== 0) setDragOverIndex(0);
              }}
              onDrop={(e) => {
                if (dragIndex === null) return;
                e.preventDefault();
                reorderRow(dragIndex, 0);
                setDragIndex(null);
                setDragOverIndex(null);
              }}
              onDragLeave={() => {
                if (dragOverIndex === 0) setDragOverIndex(null);
              }}
              className={`flex items-center pl-2 pr-1 h-12 border-b border-border transition-colors ${
                dragIndex === 0 ? "opacity-40" : ""
              } ${dragOverIndex === 0 && dragIndex !== 0 ? "bg-muted" : ""}`}
            >
              {start ? (
                <button
                  type="button"
                  draggable
                  onDragStart={(e) => {
                    setDragIndex(0);
                    e.dataTransfer.effectAllowed = "move";
                    try { e.dataTransfer.setData("text/plain", "start"); } catch { /* noop */ }
                  }}
                  onDragEnd={() => {
                    setDragIndex(null);
                    setDragOverIndex(null);
                  }}
                  aria-label="Reorder starting point"
                  title="Drag to reorder (or make this a stop)"
                  className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing touch-none"
                >
                  <GripVertical className="w-4 h-4" />
                </button>
              ) : (
                <span className="w-8 h-8 flex-shrink-0" aria-hidden />
              )}
              <input
                value={startText}
                onChange={(e) => setStartText(e.target.value)}
                onFocus={() => setActive({ kind: "start" })}
                disabled={offline}
                aria-disabled={offline}
                onKeyDown={async (e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  if (offline) return;
                  // If the autocomplete already has results, pick the top one.
                  if (suggestions.length > 0) {
                    pick(suggestions[0]);
                    (e.target as HTMLInputElement).blur();
                    return;
                  }
                  // Otherwise geocode the typed text on the fly so the map
                  // can fly to the place even before suggestions arrive.
                  const q = startText.trim();
                  if (q.length < 2) return;
                  try {
                    const r = await geocodeAutocomplete(q, userLocation);
                    if (r.length > 0) {
                      pick(r[0]);
                      (e.target as HTMLInputElement).blur();
                    }
                  } catch {
                    /* ignore */
                  }
                }}
                placeholder={offline ? "Offline — searches paused" : startPlaceholder}
                className="flex-1 min-w-0 bg-transparent outline-none text-sm placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-60"
              />
              {/* PLE-34: this used to be a plain, non-interactive <span> that
               *  only rendered once `userLocation` already existed — so on a
               *  fresh load (or while the first fix is still in flight) the
               *  empty start field showed no GPS affordance at all, and the
               *  only way to reach one was to focus the field and open the
               *  suggestions dropdown (PLE-13's "Use current location" row).
               *  Reported: "you have to start a search first" before any icon
               *  shows up. Now it's a real button from the first paint —
               *  tapping it requests location if we don't have a fix yet, or
               *  confirms/re-applies GPS as the start if we do. */}
              {!offline && !start && !startText && (
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    if (userLocation) applyCurrentLocation();
                    else onRequestLocation?.();
                  }}
                  disabled={!userLocation && isRequestingLocation}
                  aria-label={userLocation ? "Using current location" : "Use current location"}
                  title={
                    locationStatus === "denied" || locationStatus === "unavailable"
                      ? "Location is off — enable it in your settings"
                      : userLocation
                        ? "Using current location"
                        : "Use current location"
                  }
                  className="w-8 h-8 flex items-center justify-center text-primary hover:bg-muted rounded-full disabled:opacity-60"
                >
                  {isRequestingLocation && !userLocation
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <LocateFixed className="w-4 h-4" />}
                </button>
              )}
              {startText && (
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setStartText("");
                    onChange({ start: null, end, stops });
                    setActive({ kind: "start" });
                  }}
                  aria-label="Clear start"
                  title="Clear"
                  className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted rounded-full"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
              {/* One-tap "use my current location" — always in the start field so
               *  it never takes more than a tap to start from where you are
               *  (Morgan: "needs to be easier to add current location"). Shown
               *  whenever the start isn't already plain GPS. */}
              {!offline && (start || startText) && (
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    if (userLocation) applyCurrentLocation();
                    else if (onRequestLocation) onRequestLocation();
                  }}
                  disabled={!userLocation && isRequestingLocation}
                  aria-label="Use current location"
                  title="Use current location"
                  className="w-8 h-8 flex items-center justify-center text-primary hover:bg-muted rounded-full disabled:opacity-60"
                >
                  {isRequestingLocation && !userLocation
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <LocateFixed className="w-4 h-4" />}
                </button>
              )}
              {(onLabelPoint || onAddSavedPlace) && (
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => (start && onLabelPoint ? onLabelPoint(start) : onAddSavedPlace?.())}
                  aria-label="Save this place"
                  title="Save this place (Home, Work, …)"
                  className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted rounded-full"
                >
                  <Bookmark className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Stop rows */}
            {stopTexts.map((text, i) => {
              const stopPoint = stops[i];
              const hasResolvedStop = !!stopPoint && stopPoint.coords[0] !== 0 && stopPoint.coords[1] !== 0;
              return (
                <div
                  key={i}
                  onDragOver={(e) => {
                    if (dragIndex === null) return;
                    e.preventDefault();
                    if (dragOverIndex !== i + 1) setDragOverIndex(i + 1);
                  }}
                  onDrop={(e) => {
                    if (dragIndex === null) return;
                    e.preventDefault();
                    reorderRow(dragIndex, i + 1);
                    setDragIndex(null);
                    setDragOverIndex(null);
                  }}
                  onDragLeave={() => {
                    if (dragOverIndex === i + 1) setDragOverIndex(null);
                  }}
                  className={`flex items-center pl-2 pr-1 h-12 border-b border-border transition-colors ${
                    dragIndex === i + 1 ? "opacity-40" : ""
                  } ${dragOverIndex === i + 1 && dragIndex !== i + 1 ? "bg-muted" : ""}`}
                >
                  <button
                    type="button"
                    draggable
                    onDragStart={(e) => {
                      setDragIndex(i + 1);
                      e.dataTransfer.effectAllowed = "move";
                      // Required for Firefox to actually start the drag.
                      try { e.dataTransfer.setData("text/plain", String(i + 1)); } catch { /* noop */ }
                    }}
                    onDragEnd={() => {
                      setDragIndex(null);
                      setDragOverIndex(null);
                    }}
                    aria-label={`Reorder stop ${i + 1}`}
                    title="Drag to reorder"
                    className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing touch-none"
                  >
                    <GripVertical className="w-4 h-4" />
                  </button>
                  <input
                    value={text}
                    onChange={(e) => {
                      const next = [...stopTexts];
                      next[i] = e.target.value;
                      setStopTexts(next);
                    }}
                    onFocus={() => setActive({ kind: "stop", index: i })}
                    disabled={offline}
                    aria-disabled={offline}
                    placeholder={offline ? "Offline" : `Stop ${i + 1}`}
                    className="flex-1 min-w-0 bg-transparent outline-none text-sm placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-60"
                  />
                  {onLabelPoint && hasResolvedStop && (
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => onLabelPoint(stopPoint)}
                      aria-label={`Save stop ${i + 1}`}
                      title="Save this place (Home, Work, …)"
                      className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted rounded-full"
                    >
                      <Bookmark className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => removeStop(i)}
                    aria-label={`Remove stop ${i + 1}`}
                    className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted rounded-full"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}

            {/* End row */}
            <div
              onDragOver={(e) => {
                if (dragIndex === null) return;
                e.preventDefault();
                const destIdx = stopTexts.length + 1;
                if (dragOverIndex !== destIdx) setDragOverIndex(destIdx);
              }}
              onDrop={(e) => {
                if (dragIndex === null) return;
                e.preventDefault();
                reorderRow(dragIndex, stopTexts.length + 1);
                setDragIndex(null);
                setDragOverIndex(null);
              }}
              onDragLeave={() => {
                const destIdx = stopTexts.length + 1;
                if (dragOverIndex === destIdx) setDragOverIndex(null);
              }}
              className={`flex items-center pl-2 pr-1 h-12 transition-colors ${
                dragIndex === stopTexts.length + 1 ? "opacity-40" : ""
              } ${dragOverIndex === stopTexts.length + 1 && dragIndex !== stopTexts.length + 1 ? "bg-muted" : ""}`}
            >
              {hasStops && end ? (
                <button
                  type="button"
                  draggable
                  onDragStart={(e) => {
                    setDragIndex(stopTexts.length + 1);
                    e.dataTransfer.effectAllowed = "move";
                    try { e.dataTransfer.setData("text/plain", "end"); } catch { /* noop */ }
                  }}
                  onDragEnd={() => {
                    setDragIndex(null);
                    setDragOverIndex(null);
                  }}
                  aria-label="Reorder destination"
                  title="Drag to reorder (or make this a stop)"
                  className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing touch-none"
                >
                  <GripVertical className="w-4 h-4" />
                </button>
              ) : (
                <span className="w-8 h-8 flex-shrink-0" aria-hidden />
              )}
              <input
                value={endText}
                onChange={(e) => setEndText(e.target.value)}
                onFocus={() => setActive({ kind: "end" })}
                disabled={offline}
                aria-disabled={offline}
                placeholder={offline ? "Offline — reconnect to search" : "Where are you going?"}
                className="flex-1 min-w-0 bg-transparent outline-none text-sm placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-60"
              />
              {(busy || loading) && (
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground mr-1" />
              )}
              {/* Phone mic (Morgan's friction batch): the car got voice
               *  search — walkers holding a coffee get it too. Native
               *  dictation; the transcript drops straight into the field. */}
              {!endText && !offline && Capacitor.isNativePlatform() && (
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setActive({ kind: "end" });
                    setDictating(true);
                    void import("@/native/carplayBridge")
                      .then((m) => m.phoneDictation())
                      .then((text) => {
                        setDictating(false);
                        if (text === null) {
                          toast("Mic unavailable — check Settings → Pleasant → Microphone.");
                          return;
                        }
                        if (text.length > 0) setEndText(text);
                      })
                      .catch(() => setDictating(false));
                  }}
                  aria-label="Speak a destination"
                  title="Speak a destination"
                  className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted rounded-full"
                >
                  {dictating
                    ? <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    : <Mic className="w-4 h-4" />}
                </button>
              )}
              {endText && (
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setEndText("");
                    onChange({ start, end: null, stops });
                    setActive({ kind: "end" });
                  }}
                  aria-label="Clear destination"
                  title="Clear"
                  className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted rounded-full"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
              {(onLabelPoint || onAddSavedPlace) && (
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => (end && onLabelPoint ? onLabelPoint(end) : onAddSavedPlace?.())}
                  aria-label="Save this place"
                  title="Save this place (Home, Work, …)"
                  className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted rounded-full"
                >
                  <Bookmark className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={addStop}
                aria-label="Add stop"
                title="Add another stop"
                className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted rounded-full"
              >
                <Plus className="w-4 h-4" />
              </button>
              {hasStops && (
                <button
                  type="button"
                  onClick={() => { setActive(null); setCollapsed(true); }}
                  aria-label="Minimize stops"
                  title="Minimize to see the map"
                  className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted rounded-full"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Reverse + Go column — pinned to the right edge of the card so
           *  it stays anchored while inputs grow, suggestions render, or
           *  any field is focused. */}
          <div className="flex flex-col flex-shrink-0 border-l border-border ml-auto self-stretch relative z-10 bg-card">
            <button
              type="button"
              onClick={reverse}
              disabled={!start && !end}
              aria-label="Reverse route"
              title="Reverse start and end"
              className="w-14 flex-1 flex items-center justify-center bg-card hover:bg-muted text-foreground/80 disabled:opacity-40 disabled:cursor-not-allowed border-b border-border transition-smooth"
            >
              <ArrowUpDown className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={handleGo}
              disabled={!canGo || loading}
              aria-label={offline ? "Routes paused — offline" : "Find routes"}
              title={offline ? "You're offline — reconnect to plan a route" : "Find routes"}
              className="w-14 flex-1 flex items-center justify-center bg-pleasant text-primary-foreground disabled:bg-pleasant/70 disabled:cursor-not-allowed hover:brightness-110 transition-smooth"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Navigation className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Saved-place chips: only visible while the user is actively choosing
       *  a destination/start/stop, so they don't overlap the mode selector
       *  on the resting map. */}
      {active && (
        <SavedPlacesChips
          onPick={pickSaved}
          onPickHearted={pickHearted}
          onPickTrip={pickTrip}
          onEdit={onEditSavedPlace}
          onDeleteTrip={removeTrip}
          onAdd={onAddSavedPlace}
        />
      )}

      {/* "Save this trip" — used to render here as a separate floating
       *  pill, but it crowded the top-of-map chrome. Removed; saving a
       *  trip is now handled from the bottom sheet / My Routes flow. */}

      {/* "No places found" hint — shown when the user has typed a real
       *  query (>=3 chars) and we've finished fetching but Mapbox returned
       *  nothing AND we have no saved/recent matches to fall back on.
       *  Without this the dropdown silently closes and the user thinks the
       *  search is broken. Real-drive UX audit (#56b): no feedback after a
       *  query that doesn't match anything in the geocoder. */}
      {active
        && !busy
        && query.trim().length >= 3
        && suggestions.length === 0
        && savedMatches.length === 0
        && recentMatches.length === 0 && (
        <div className="mt-2 bg-card border border-border rounded-2xl shadow-elevated px-4 py-3 text-sm text-muted-foreground animate-fade-in">
          {offline
            ? "You appear to be offline — search will work again once you're back online."
            : "No places match that. Try a more specific name, or check the spelling."}
        </div>
      )}

      {/* Suggestions dropdown */}
      {/* `|| onRequestLocation` matches the row's own condition below. Without
        * it a user with no fix yet (fresh iOS install, now that launch no
        * longer asks — PLE-13) got no dropdown at all, so the always-first
        * "Use current location" row could never appear. */}
      {active && (savedMatches.length > 0 || recentMatches.length > 0 || suggestions.length > 0 || (active.kind === "start" && (userLocation || onRequestLocation))) && (
        <ul className="mt-2 bg-card border border-border rounded-2xl shadow-elevated overflow-hidden animate-fade-in max-h-72 overflow-y-auto">
          {/* Current location is ALWAYS the first row for the start field
           *  (Morgan: "current location should always be the first option —
           *  it's scary to be in traffic and not be able to do this").
           *  Tapping it re-plans from the driver's GPS immediately. */}
          {active.kind === "start" && (userLocation || onRequestLocation) && (
            <li className="border-b border-border/60">
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  if (userLocation) {
                    applyCurrentLocation();
                  } else if (onRequestLocation) {
                    onRequestLocation();
                  }
                }}
                disabled={!userLocation && isRequestingLocation}
                className="w-full text-left px-4 py-3 hover:bg-muted transition-smooth flex items-start gap-3 disabled:opacity-60 bg-primary/5"
              >
                <LocateFixed className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
                <div className="min-w-0">
                  <div className="font-medium text-sm">
                    {userLocation
                      ? "Current location"
                      : isRequestingLocation
                        ? "Locating…"
                        : "Use current location"}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {userLocation
                      ? end
                        ? "Re-plan this trip from where you are now"
                        : "Use your GPS as the starting point"
                      : locationStatus === "denied" || locationStatus === "unavailable"
                        ? "Location is off — enable it in your browser settings"
                        : "Tap to allow location access"}
                  </div>
                </div>
              </button>
            </li>
          )}
          {/* RECENTS FIRST (Morgan: "recent trips to be the first on the
           *  list... above the other options below the search bar") — the
           *  place you went last week is the likeliest place you're going
           *  now. Saved places follow, then the map's suggestions. */}
          {recentMatches.length > 0 && (
            <li className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground bg-card sticky top-0">
              Recent
            </li>
          )}
          {recentMatches.map((r) => (
            <li key={`recent-${r.id}`}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => applyRecentToActive(r)}
                className="w-full text-left px-4 py-3 hover:bg-muted transition-smooth flex items-start gap-3"
              >
                <Clock className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">{r.name || r.place}</div>
                  <div className="text-xs text-muted-foreground truncate">{r.place}</div>
                </div>
              </button>
            </li>
          ))}
          {savedMatches.map((p) => (
            <li key={`saved-${p.id}`}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => applySavedToActive(p)}
                className="w-full text-left px-4 py-3 hover:bg-muted transition-smooth flex items-start gap-3 bg-muted/30"
              >
                <span className="w-4 h-4 mt-0.5 flex items-center justify-center text-base leading-none flex-shrink-0">
                  {p.icon ?? iconFor(p.label)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm flex items-center gap-1.5">
                    {p.label}
                    <Star className="w-3 h-3 text-pleasant" fill="currentColor" />
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{p.place}</div>
                </div>
              </button>
            </li>
          ))}
          {/* (Current-location row moved to the TOP of this list — see above.) */}
          {suggestions.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(s)}
                className="w-full text-left px-4 py-3 hover:bg-muted transition-smooth flex items-start gap-3"
              >
                <MapPin className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">
                    {s.name}
                    {typeof s.rating === "number" && s.rating > 0 && (
                      <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                        {s.rating.toFixed(1)}★
                        {s.openNow === true && <span className="text-primary"> · Open</span>}
                        {s.openNow === false && " · Closed"}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{s.place}</div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
